import api from './config';
import { trackEvent } from '@/utils/analytics';

/**
 * reco-comment-replies — a circle's thread on a reco.
 *
 * The circle is never optional: a reco lives in several circles at once
 * (multi-select is live in the share screen), and the whole point of the design
 * is that the conversations stay watertight. A thread read without its circle
 * would mix audiences — the one thing circle-scoped replies exist to prevent.
 */

/** Mirrors the backend guard, which mirrors the schema's maxLength. */
export const REPLY_MAX_LENGTH = 300;

export interface ThreadComment {
  id: number;
  documentId?: string;
  content: string;
  createdAt?: string;
  author?: { id: number; username: string; avatar_url: string | null } | null;
  group?: { id: number } | null;
}

/**
 * The thread of ONE circle, legend included (it carries no circle and is the
 * same everywhere). Never throws: an unreadable thread shows as empty rather
 * than taking the screen down.
 */
export async function fetchThread(recoId: number, groupId: number): Promise<ThreadComment[]> {
  try {
    const res = await api.get(`/recommendation/${recoId}/comments?groupId=${groupId}`);
    return Array.isArray(res.data?.data) ? res.data.data : [];
  } catch {
    return [];
  }
}

/**
 * Post a reply. Validates locally FIRST so the limit is enforced where the user
 * can see it, and DOES throw on failure — the caller keeps the draft rather
 * than losing what was typed.
 */
export async function postReply(
  recoId: number,
  groupId: number,
  content: string,
): Promise<ThreadComment> {
  const text = content.trim();
  if (!text) throw new Error('empty reply');
  if (text.length > REPLY_MAX_LENGTH) throw new Error('reply too long');

  const res = await api.post(`/recommendation/${recoId}/comments`, { groupId, content: text });
  trackEvent('Comment', 'post');
  return res.data?.data as ThreadComment;
}
